import {
    AlertTriangle,
    ArrowRight,
    BadgeIndianRupee,
    CheckCircle2,
    ChevronLeft,
    CircleAlert,
    LoaderCircle,
    MessageSquareText,
    SearchCheck,
    ShieldCheck,
    ShieldX,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { FaCircleUser } from 'react-icons/fa6'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './DisputeDetailScreen.module.css'

const DISPUTE_STATUS_CONFIG = {
    OPEN: { label: 'Open', color: '#E65100', bg: '#FFF3E0' },
    UNDER_REVIEW: { label: 'Under Review', color: '#1565C0', bg: '#E3F2FD' },
    RESOLVED: { label: 'Resolved', color: '#2E7D32', bg: '#E8F5E9' },
    OVERRIDDEN: { label: 'Overridden', color: '#6A1B9A', bg: '#F3E5F5' },
    REJECTED: { label: 'Rejected', color: '#C62828', bg: '#FFEBEE' },
}

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const formatINR = (amount, minimumFractionDigits = 0, maximumFractionDigits = 0) => {
    const value = Number(amount)
    if (!Number.isFinite(value)) return '—'
    return value.toLocaleString('en-IN', {
        minimumFractionDigits,
        maximumFractionDigits,
    })
}

const formatDateTime = (value) => {
    if (!value) return '—'
    const parsed = String(value).includes(' ') ? String(value).replace(' ', 'T') : String(value)
    const date = new Date(parsed)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

const getAvailableActions = (currentDispute, runStatus) => {
    if (!currentDispute) return []
    const actions = []
    const isFinalized = String(runStatus || '').toUpperCase() === 'FINALIZED'
    const disputeStatus = String(currentDispute?.disputeStatus || '').toUpperCase()

    if (disputeStatus === 'OPEN') {
        actions.push({ label: 'Mark Under Review', status: 'UNDER_REVIEW', style: 'secondary', icon: SearchCheck })
    }

    if (['OPEN', 'UNDER_REVIEW'].includes(disputeStatus)) {
        if (!isFinalized && currentDispute?.ledgerIdentifier) {
            actions.push({
                label: 'Resolve & Apply Correction',
                status: 'RESOLVED',
                style: 'primary',
                icon: CheckCircle2,
            })
        } else {
            actions.push({
                label: 'Mark Resolved (Note Only)',
                status: 'RESOLVED',
                style: 'primary',
                icon: CheckCircle2,
            })
        }

        actions.push({ label: 'Override — Keep Original', status: 'OVERRIDDEN', style: 'secondary', icon: ShieldCheck })
        actions.push({ label: 'Reject', status: 'REJECTED', style: 'destructive', icon: ShieldX })
    }

    return actions
}

const DisputeDetailScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { disputeIdentifier } = useParams()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const seededDispute = location?.state?.dispute || null

    const [dispute, setDispute] = useState(seededDispute)
    const [isLoading, setIsLoading] = useState(!seededDispute)
    const [targetStatus, setTargetStatus] = useState(null)
    const [resolutionNote, setResolutionNote] = useState('')
    const [correctionAmount, setCorrectionAmount] = useState('')
    const [noteError, setNoteError] = useState(null)
    const [isResolving, setIsResolving] = useState(false)
    const [result, setResult] = useState(null)

    const fetchDisputeDetail = useCallback(async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier || !disputeIdentifier) {
            setIsLoading(false)
            return
        }

        try {
            setIsLoading(true)

            const response = await fetch(BASE_URL_API + 'getPayslipDisputeDetail', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    disputeIdentifier,
                }),
            })

            const respo = await response.json()

            if (respo?.status === 'success') {
                setDispute(respo.response || null)
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            if (!seededDispute) {
                toast.error(respo?.message || 'Failed to load dispute detail.')
            }
        } catch {
            if (!seededDispute) toast.error('Network error.')
        } finally {
            setIsLoading(false)
        }
    }, [activeStore?.storeIdentifier, authCtx, disputeIdentifier, seededDispute])

    useEffect(() => {
        if (!seededDispute) {
            fetchDisputeDetail()
            return
        }

        setDispute(seededDispute)
        setIsLoading(false)
    }, [fetchDisputeDetail, seededDispute])

    useEffect(() => {
        if (!dispute) return
        setCorrectionAmount(
            Number.isFinite(Number(dispute?.expectedAmount)) ? String(Number(dispute?.expectedAmount)) : ''
        )
    }, [dispute?.expectedAmount, dispute?.disputeIdentifier])

    const runStatusKey = String(dispute?.runStatus || '').toUpperCase()
    const disputeStatusKey = String(dispute?.disputeStatus || '').toUpperCase()
    const isFinalized = runStatusKey === 'FINALIZED'
    const statusMeta = DISPUTE_STATUS_CONFIG[disputeStatusKey] || {
        label: disputeStatusKey || '—',
        color: '#475569',
        bg: '#E2E8F0',
    }

    const delta = useMemo(() => {
        const expected = Number(dispute?.expectedAmount || 0)
        const shown = Number(dispute?.disputedAmount || 0)
        return expected - shown
    }, [dispute?.disputedAmount, dispute?.expectedAmount])

    const availableActions = useMemo(() => getAvailableActions(dispute, runStatusKey), [dispute, runStatusKey])

    const shouldShowCorrectionInput =
        !isFinalized &&
        Boolean(dispute?.ledgerIdentifier) &&
        ['OPEN', 'UNDER_REVIEW'].includes(disputeStatusKey)

    const resolveDispute = useCallback(
        async (status) => {
            const isTerminalAction = ['RESOLVED', 'OVERRIDDEN', 'REJECTED'].includes(status)

            if (!dispute?.disputeIdentifier) {
                toast.error('Dispute context is missing.')
                return
            }

            if (isTerminalAction && !resolutionNote.trim()) {
                setNoteError('Resolution note is required.')
                return
            }

            setNoteError(null)

            try {
                setIsResolving(true)
                setTargetStatus(status)

                const body = {
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    disputeIdentifier: dispute.disputeIdentifier,
                    targetStatus: status,
                    resolvedBy: authCtx?.staffIdentifier || authCtx?.authIdentifier || '',
                    resolutionNote: resolutionNote.trim(),
                }

                if (status === 'RESOLVED' && correctionAmount !== '') {
                    const amount = Number.parseFloat(correctionAmount)
                    if (Number.isFinite(amount) && amount >= 0) {
                        body.correctionAmount = amount
                    }
                }

                const response = await fetch(BASE_URL_API + 'resolvePayslipDispute', {
                    method: 'POST',
                    body: JSON.stringify(body),
                })

                const respo = await response.json()

                if (respo?.status === 'success') {
                    setResult(respo.response || null)
                    setDispute((prev) => {
                        if (!prev) return prev
                        return {
                            ...prev,
                            disputeStatus: respo?.response?.newStatus || prev?.disputeStatus,
                            resolvedBy: respo?.response?.resolvedBy || prev?.resolvedBy,
                            resolvedOn: respo?.response?.resolvedOn || prev?.resolvedOn,
                            resolutionNote: resolutionNote.trim() || prev?.resolutionNote || '',
                        }
                    })

                    if (respo?.response?.correctionApplied) {
                        toast.success(
                            `Correction of ₹${formatINR(respo?.response?.correction?.delta, 2, 2)} applied.`
                        )
                    } else {
                        toast.success(respo?.message || `Dispute ${status.toLowerCase()}.`)
                    }
                    return
                }

                if (respo?.message === 'tokenExpired') {
                    authCtx.logout()
                    return
                }

                switch (respo?.errorCode) {
                    case 'PAY_002':
                    case 'VAL_003':
                    case 'VAL_007':
                    case 'SYS_002':
                        toast.error(respo?.message || 'Resolution failed.')
                        break
                    case 'VAL_001':
                        if (String(respo?.message || '').includes('resolutionNote')) {
                            setNoteError(respo?.message || 'Resolution note is required.')
                        } else {
                            toast.error(respo?.message || 'Resolution failed.')
                        }
                        break
                    default:
                        toast.error(respo?.message || 'Resolution failed.')
                }
            } catch {
                toast.error('Network error.')
            } finally {
                setIsResolving(false)
                setTargetStatus(null)
            }
        },
        [activeStore?.storeIdentifier, authCtx, correctionAmount, dispute?.disputeIdentifier, resolutionNote]
    )

    if (!disputeIdentifier && !seededDispute?.disputeIdentifier) {
        return (
            <div className={styles.page}>
                <div className={styles.inlineAlertWarn}>
                    <AlertTriangle size={16} />
                    <span>Dispute identifier is missing.</span>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ChevronLeft size={18} />
                </button>
                <div className={styles.headerMeta}>
                    <h1 className={styles.headerTitle}>Dispute Detail</h1>
                    <p className={styles.headerSub}>{dispute?.cycleLabel || 'Payroll dispute record'}</p>
                </div>
                <span className={styles.statusPill} style={{ color: statusMeta.color, backgroundColor: statusMeta.bg }}>
                    {statusMeta.label}
                </span>
            </div>

            {isLoading ? (
                <div className={styles.loaderCard}>
                    <LoaderCircle size={20} className={styles.spinIcon} />
                    <span>Loading dispute details...</span>
                </div>
            ) : !dispute ? (
                <div className={styles.inlineAlertWarn}>
                    <CircleAlert size={16} />
                    <span>Unable to load dispute detail.</span>
                </div>
            ) : (
                <>
                    <section className={styles.summaryCard}>
                        <div className={styles.staffIdentity}>
                            <FaCircleUser className={styles.avatarIcon} />
                            <div>
                                <strong>{dispute?.staffName || 'Staff'}</strong>
                                <span>{dispute?.cycleLabel || 'Cycle'}</span>
                            </div>
                        </div>

                        <div className={styles.metaGrid}>
                            <div className={styles.metaCell}>
                                <span>Component</span>
                                <strong>{dispute?.disputedComponent || '—'}</strong>
                            </div>
                            <div className={styles.metaCell}>
                                <span>Payslip shows</span>
                                <strong>₹{formatINR(dispute?.disputedAmount, 2, 2)}</strong>
                            </div>
                            <div className={styles.metaCell}>
                                <span>Staff expects</span>
                                <strong>₹{formatINR(dispute?.expectedAmount, 2, 2)}</strong>
                            </div>
                            <div className={styles.metaCell}>
                                <span>Difference</span>
                                <strong style={{ color: delta >= 0 ? '#15803d' : '#b91c1c' }}>
                                    {delta >= 0 ? '+' : '−'}₹{formatINR(Math.abs(delta), 2, 2)}
                                </strong>
                            </div>
                        </div>
                    </section>

                    <section className={styles.reasonCard}>
                        <div className={styles.reasonHead}>
                            <MessageSquareText size={15} />
                            <strong>Dispute Reason</strong>
                        </div>
                        <p>{dispute?.disputeReason || 'No dispute reason provided.'}</p>
                    </section>

                    <section className={`${styles.contextCard} ${isFinalized ? styles.contextWarn : styles.contextSuccess}`}>
                        {isFinalized ? <AlertTriangle size={15} /> : <CheckCircle2 size={15} />}
                        <span>
                            {isFinalized
                                ? 'Run is finalized — no value changes possible. Correction applies in the next cycle.'
                                : 'Run is in DRAFT/REVIEW — override corrections can be applied to this run.'}
                        </span>
                    </section>

                    {['OPEN', 'UNDER_REVIEW'].includes(disputeStatusKey) ? (
                        <section className={styles.formCard}>
                            <div className={styles.formHead}>
                                <strong>Resolution</strong>
                                <span>Choose an action and document what was verified.</span>
                            </div>

                            {shouldShowCorrectionInput ? (
                                <div className={styles.fieldBlock}>
                                    <label htmlFor="correctionAmount">Correction amount (₹)</label>
                                    <input
                                        id="correctionAmount"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={correctionAmount}
                                        onChange={(event) => setCorrectionAmount(event.target.value)}
                                        placeholder={String(dispute?.expectedAmount || '')}
                                    />
                                    <small>Leave unchanged to accept staff's expected amount.</small>
                                </div>
                            ) : null}

                            <div className={styles.fieldBlock}>
                                <label htmlFor="resolutionNote">Resolution Note *</label>
                                <textarea
                                    id="resolutionNote"
                                    rows={4}
                                    value={resolutionNote}
                                    onChange={(event) => {
                                        setResolutionNote(event.target.value)
                                        if (noteError) setNoteError(null)
                                    }}
                                    placeholder="Explain your verification and final decision..."
                                />
                                {noteError ? <span className={styles.fieldError}>{noteError}</span> : null}
                            </div>

                            <div className={styles.actionGrid}>
                                {availableActions.map((action) => {
                                    const Icon = action.icon
                                    const className =
                                        action.style === 'primary'
                                            ? styles.primaryBtn
                                            : action.style === 'destructive'
                                                ? styles.destructiveBtn
                                                : styles.secondaryBtn

                                    return (
                                        <button
                                            key={action.status}
                                            type="button"
                                            className={className}
                                            onClick={() => resolveDispute(action.status)}
                                            disabled={isResolving}
                                        >
                                            {isResolving && targetStatus === action.status ? (
                                                <>
                                                    <LoaderCircle size={14} className={styles.spinIcon} /> Processing...
                                                </>
                                            ) : (
                                                <>
                                                    <Icon size={14} />
                                                    {action.label}
                                                    {action.style === 'primary' ? <ArrowRight size={13} /> : null}
                                                </>
                                            )}
                                        </button>
                                    )
                                })}
                            </div>
                        </section>
                    ) : null}

                    {(result || !['OPEN', 'UNDER_REVIEW'].includes(disputeStatusKey)) ? (
                        <section className={styles.resultCard}>
                            <div className={styles.resultHead}>
                                <BadgeIndianRupee size={15} />
                                <strong>Resolution Result</strong>
                            </div>

                            {String(result?.newStatus || dispute?.disputeStatus || '').toUpperCase() === 'UNDER_REVIEW' ? (
                                <p className={styles.resultText}>Marked as under review. Continue investigating.</p>
                            ) : null}

                            {String(result?.newStatus || dispute?.disputeStatus || '').toUpperCase() === 'RESOLVED' ? (
                                <>
                                    {result?.correctionApplied ? (
                                        <div className={styles.resultSuccessPanel}>
                                            <strong>✓ Correction applied</strong>
                                            <span>
                                                ₹{formatINR(result?.correction?.previousValue, 2, 2)} → ₹
                                                {formatINR(result?.correction?.newValue, 2, 2)}
                                                {'  '}
                                                ({result?.correction?.delta >= 0 ? '+' : '−'}₹
                                                {formatINR(Math.abs(Number(result?.correction?.delta || 0)), 2, 2)})
                                            </span>
                                            {result?.correction?.updatedSummary ? (
                                                <span>
                                                    Net pay updated: ₹
                                                    {formatINR(
                                                        Number(result?.correction?.updatedSummary?.netPay || 0) -
                                                            Number(result?.correction?.updatedSummary?.netPayDelta || 0),
                                                        2,
                                                        2
                                                    )}
                                                    {' → '}₹
                                                    {formatINR(result?.correction?.updatedSummary?.netPay, 2, 2)}
                                                </span>
                                            ) : null}
                                        </div>
                                    ) : (
                                        <p className={styles.resultText}>
                                            ✓ Dispute noted. Correction will be applied in the next payroll cycle.
                                        </p>
                                    )}
                                </>
                            ) : null}

                            {String(result?.newStatus || dispute?.disputeStatus || '').toUpperCase() === 'OVERRIDDEN' ? (
                                <p className={styles.resultText}>Original amount kept. {resolutionNote || dispute?.resolutionNote || ''}</p>
                            ) : null}

                            {String(result?.newStatus || dispute?.disputeStatus || '').toUpperCase() === 'REJECTED' ? (
                                <p className={styles.resultText}>Dispute rejected. {resolutionNote || dispute?.resolutionNote || ''}</p>
                            ) : null}

                            <div className={styles.resultMetaGrid}>
                                <div className={styles.resultMetaCell}>
                                    <span>Resolved By</span>
                                    <strong>{result?.resolvedBy || dispute?.resolvedBy || '—'}</strong>
                                </div>
                                <div className={styles.resultMetaCell}>
                                    <span>Resolved On</span>
                                    <strong>{formatDateTime(result?.resolvedOn || dispute?.resolvedOn)}</strong>
                                </div>
                            </div>
                        </section>
                    ) : null}
                </>
            )}
        </div>
    )
}

export default DisputeDetailScreen